import axios from 'axios';
import { useCallback, useState } from 'react';
import { ppaSimulationUrls } from 'config/urls';
import { sendNotification } from 'utils/helperMethods';
import type { SimulatorAppliedFilters, SimulatorFilterCategory } from './SimulatorFilterPanel';
import {
    buildSimulatorTableRequestPayload,
    type SimulatorFilterScopeItem,
    type SimulatorTableSearchInput,
} from './simulatorFilter.helpers';
import { resolveSimulatorTableFiltersViaApplyFilter } from './simulatorFilterApply.helpers';

type ApplySimulatorFiltersArgs = {
    projectId: number | string;
    scenarioId: number | string;
    scenarioTabId: number | string | null;
    filterCategories: SimulatorFilterCategory[];
    appliedScope?: SimulatorFilterScopeItem[];
    appliedFilters: SimulatorAppliedFilters;
    searchInput?: SimulatorTableSearchInput;
};

export function useApplySimulatorFilters() {
    const [isApplying, setIsApplying] = useState(false);

    const applySimulatorFilters = useCallback(
        async ({
            projectId,
            scenarioId,
            scenarioTabId,
            filterCategories,
            appliedScope,
            appliedFilters,
            searchInput,
        }: ApplySimulatorFiltersArgs): Promise<boolean> => {
            if (scenarioTabId == null) {
                sendNotification('error', 'Active lens is unavailable. Select a lens tab and try again.');
                return false;
            }

            setIsApplying(true);
            try {
                const resolvedFilters = await resolveSimulatorTableFiltersViaApplyFilter({
                    filterCategories,
                    categorySelections: appliedFilters.categorySelections,
                    appliedScope,
                });

                const payload = buildSimulatorTableRequestPayload({
                    projectId,
                    scenarioId,
                    resolvedFilters,
                    appliedFilters,
                    searchInput,
                });

                await axios.post(ppaSimulationUrls.simulatorTableUrl, payload);
                return true;
            } catch (error: unknown) {
                const detail =
                    (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail?.trim() ||
                    'Failed to apply filters. Please try again.';
                sendNotification('error', detail);
                return false;
            } finally {
                setIsApplying(false);
            }
        },
        [],
    );

    return {
        applySimulatorFilters,
        isApplying,
    };
}
